'use client';

import { PublicMoment } from '@/lib/services/public-moments-service';
import { Card } from '@/components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Clock, Play } from 'lucide-react';
import Link from 'next/link';
import { cn } from '@/lib/utils';
import { formatDistanceToNow } from 'date-fns';

interface PublicMomentCardProps {
  moment: PublicMoment;
  className?: string;
}

export default function PublicMomentCard({ moment, className }: PublicMomentCardProps) {
  const formatDuration = (seconds?: number) => {
    if (!seconds) return '';
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };
  
  const postUrl = `/@${moment.author_handle}/posts/${moment.id}?from=home`;
  
  return (
    <Link href={postUrl}>
      <Card className={cn(
        "group cursor-pointer overflow-hidden transition-all duration-300",
        "hover:scale-[1.02] hover:shadow-xl",
        className
      )}>
        {/* Thumbnail Container */}
        <div className="relative aspect-video bg-muted overflow-hidden">
          {moment.thumbnail_url ? (
            <img
              src={moment.thumbnail_url}
              alt={moment.title}
              className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
            />
          ) : (
            <div className="w-full h-full bg-gradient-to-br from-purple-600 to-pink-600" />
          )}
          
          {/* Play Overlay */}
          <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors duration-300 flex items-center justify-center">
            <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-300">
              <div className="bg-white/90 backdrop-blur rounded-full p-4 shadow-lg">
                <Play className="h-8 w-8 text-black fill-black" />
              </div>
            </div>
          </div>
          
          {/* Duration Badge */}
          {moment.duration > 0 && (
            <div className="absolute bottom-2 right-2 bg-black/80 backdrop-blur text-white text-xs px-2 py-1 rounded">
              {formatDuration(moment.duration)}
            </div>
          )}
        </div>
        
        {/* Content */}
        <div className="p-4 space-y-3">
          {/* Title */}
          <h3 className="font-semibold line-clamp-2 group-hover:text-primary transition-colors">
            {moment.title}
          </h3>
          
          {/* Author Info */}
          <div className="flex items-center gap-3">
            <Avatar className="h-8 w-8">
              <AvatarImage src={moment.author_avatar || undefined} />
              <AvatarFallback>{moment.author_name.charAt(0).toUpperCase()}</AvatarFallback>
            </Avatar>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium truncate">{moment.author_name}</p>
              <p className="text-xs text-muted-foreground">
                {moment.domain_name} • {formatDistanceToNow(new Date(moment.publish_at || moment.created_at), { addSuffix: true })}
              </p>
            </div>
          </div>
          
          {/* Topics */}
          {moment.ai_topics && moment.ai_topics.length > 0 && (
            <div className="flex flex-wrap gap-1">
              {moment.ai_topics.slice(0, 3).map((topic) => (
                <span
                  key={topic}
                  className="text-xs bg-muted px-2 py-1 rounded-full"
                  onClick={(e) => {
                    e.preventDefault();
                    window.location.href = `/home?category=${encodeURIComponent(topic)}`;
                  }}
                >
                  {topic}
                </span>
              ))}
              {moment.ai_topics.length > 3 && (
                <span className="text-xs text-muted-foreground">
                  +{moment.ai_topics.length - 3}
                </span>
              )}
            </div>
          )}
        </div>
      </Card>
    </Link>
  );
}